import type { MapLayerMouseEvent } from 'mapbox-gl';
import { useEffect } from 'react';
import type { MapRef } from 'react-map-gl';
import { GROUP_RING_LAYERS, pinRingOpacityExpression } from '@/components/Dashboard/markerPulse';

// Founder 2026-09-21 (production review, round 2): the forests and farmers GROUP
// markers get the same hover / selection ripple as the pins. They live on the
// overview (and forests-group also on a tree-pin card), a different app state
// from the clustered pins, so useMarkerPulse (keyed to a pin source) does not
// cover them - this is its group twin.
//
// Structure mirrors useMarkerPulse's hover/select portion (kept separate rather
// than folded in, to leave that tested hook untouched, S2): the ONE marker the
// pointer is on (hover) or that was last clicked (selection, until a click on
// empty map) ripples its `<layer>-ring`; everything else rests at opacity 0.
// No clustering, no density, no zoom cap - group markers show at every zoom.
//
// Note: clicking a group marker navigates into it (useMapEvents handleGroupClick),
// which unmounts the group layer, so selection is effectively a desktop-hover
// affordance; the selection path is kept for parity and does no harm.
const NEVER_MS = 1e12;
type Target = { source: string; id: number } | null;

export const useGroupRipple = (mapRef: React.RefObject<MapRef | null>, enabled: boolean) => {
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!enabled || !map) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let onScreen = true;
    let active: Target = null;
    let hovered: Target = null;
    let selected: Target = null;
    let lastPaint = 0;
    const MIN_PAINT_INTERVAL_MS = 32;
    const ringLayers = GROUP_RING_LAYERS.map((l) => `${l}-ring`);

    // Ripple the active ring layer; the expression yields 0 for any feature
    // without a {start,end} feature-state, so non-active markers stay invisible.
    const paint = (now: number) => {
      for (const ring of ringLayers) {
        if (map.getLayer(ring)) {
          map.setPaintProperty(ring, 'circle-stroke-opacity', pinRingOpacityExpression(now, 1), {
            validate: false,
          });
        }
      }
    };
    const resetRings = () => {
      for (const ring of ringLayers) {
        if (map.getLayer(ring)) {
          map.setPaintProperty(ring, 'circle-stroke-opacity', 0, { validate: false });
        }
      }
    };

    const same = (a: Target, b: Target) =>
      a === b || (!!a && !!b && a.source === b.source && a.id === b.id);

    const apply = (now: number) => {
      const next = hovered ?? selected;
      if (same(next, active)) return;
      if (active && map.getSource(active.source)) {
        map.removeFeatureState({ source: active.source, id: active.id });
      }
      if (next && map.getSource(next.source)) {
        map.setFeatureState({ source: next.source, id: next.id }, { start: now, end: NEVER_MS });
      }
      active = next;
    };

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      if (now - lastPaint < MIN_PAINT_INTERVAL_MS) return;
      lastPaint = now;
      const hadActive = active !== null;
      apply(now);
      if (active === null && !hadActive) return;
      if (active === null) {
        resetRings();
        return;
      }
      paint(now);
    };

    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };
    const clear = () => {
      if (active && map.getSource(active.source)) {
        map.removeFeatureState({ source: active.source, id: active.id });
      }
      active = null;
      resetRings();
    };
    const sync = () => {
      stop();
      if (reducedMotion.matches) clear();
      else if (onScreen) frame = requestAnimationFrame(tick);
    };

    const targetOf = (source: string, e: MapLayerMouseEvent): Target => {
      const id = e.features?.[0]?.properties?.pulse_id;
      return typeof id === 'number' ? { source, id } : null;
    };
    const handlers = GROUP_RING_LAYERS.map((source) => {
      const onMove = (e: MapLayerMouseEvent) => {
        hovered = targetOf(source, e);
      };
      const onLeave = () => {
        hovered = null;
      };
      const onClick = (e: MapLayerMouseEvent) => {
        selected = targetOf(source, e);
        e.originalEvent.preventDefault();
      };
      map.on('mousemove', source, onMove);
      map.on('mouseleave', source, onLeave);
      map.on('click', source, onClick);
      return { source, onMove, onLeave, onClick };
    });
    const onMapClick = (e: MapLayerMouseEvent) => {
      if (!e.originalEvent.defaultPrevented) selected = null;
    };
    map.on('click', onMapClick);

    const observer = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      sync();
    });
    observer.observe(map.getContainer());
    reducedMotion.addEventListener('change', sync);
    sync();

    return () => {
      stop();
      clear();
      observer.disconnect();
      reducedMotion.removeEventListener('change', sync);
      for (const h of handlers) {
        map.off('mousemove', h.source, h.onMove);
        map.off('mouseleave', h.source, h.onLeave);
        map.off('click', h.source, h.onClick);
      }
      map.off('click', onMapClick);
    };
  }, [mapRef, enabled]);
};
